import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FiMinimize2, FiMaximize2 } from "react-icons/fi";
import { IoSchoolSharp } from "react-icons/io5";
import { MdWork } from "react-icons/md";
import { RxDotFilled } from "react-icons/rx";

import experiences from "../../assets/docs/experiences.json";
import { showCursor, hideCursor, expandCursor } from "../../utility/mouse";

export default function Experiences() {
  return (
    <Container>
      <h3>Steps Along the Way</h3>

      <div className="parent">
        <Slider>
          {new Array(6).fill("").map((_, index) => (
            <span key={index} />
          ))}
        </Slider>

        <div className="wrapper">
          {experiences.map((item, index) => (
            <ExperienceBox item={item} key={index} />
          ))}
        </div>
      </div>
    </Container>
  );
}

function ExperienceBox({ item = {} }) {
  const boxRef = useRef(null);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!showMore || !boxRef.current) return;

    const start = window.scrollY;
    const rect = boxRef.current.getBoundingClientRect();

    const target = rect.top + window.scrollY - window.innerHeight / 6;

    const distance = target - start;
    const duration = 850;

    let startTime = null;
    let animationFrame;

    const easeInOut = (t) => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animateScroll = (currentTime) => {
      if (!startTime) {
        startTime = currentTime;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      window.scrollTo(0, start + distance * easeInOut(progress));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animateScroll);
      }
    };

    animationFrame = requestAnimationFrame(animateScroll);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [showMore]);

  function handleBoxClick() {
    expandCursor();
    setShowMore((prev) => !prev);
  }

  return (
    <BoxContainer>
      <div className="box-cover">
        <span className="indicator" />

        <motion.div
          className="box"
          ref={boxRef}
          onMouseMove={showCursor}
          onMouseLeave={hideCursor}
          onClick={handleBoxClick}
        >
          <span className="mini-max-ico">
            {showMore ? <FiMinimize2 /> : <FiMaximize2 />}
          </span>

          <div className="less-desc">
            <div className="where-how-long">
              <span>
                {item.education ? <IoSchoolSharp /> : <MdWork />}
                {item.company}
              </span>

              <span>{item.duration}</span>
            </div>

            {item.roles ? (
              <MultipleRoles roles={item.roles} />
            ) : (
              <span>{item.role}</span>
            )}
          </div>

          <AnimatePresence initial={false}>
            {showMore && (
              <motion.div
                className="work-done"
                initial={{
                  height: 0,
                  opacity: 0,
                }}
                animate={{
                  height: "auto",
                  opacity: 1,
                }}
                exit={{
                  height: 0,
                  opacity: 0,
                }}
                transition={{
                  duration: 0.35,
                  ease: "easeInOut",
                }}
              >
                <div className="work-done-inner">
                  {item.techs && (
                    <ul className="techs">
                      {item.techs.map((tech, index) => (
                        <li key={index}>#{tech}</li>
                      ))}
                    </ul>
                  )}

                  {item.work && <p>{item.work}</p>}

                  {item.courses && (
                    <p className="courses">
                      <span>Relevant Courses:</span>
                      {item.courses}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </BoxContainer>
  );
}

function MultipleRoles({ roles = [] }) {
  return roles.map((item, idx) => (
    <div key={idx} className="multiple-roles">
      <RxDotFilled />

      <span>{item[0]}</span>

      <span className="separate">|</span>

      <span>{item[1]}</span>
    </div>
  ));
}

const Container = styled.div`
  width: 100%;
  position: relative;

  h3 {
    font-family: "Space Mono", monospace;
    color: #fff;
    font-weight: 400;
    text-align: center;
    font-size: 44px;
  }

  .parent {
    margin-top: 100px;
    padding: 35px 0 70px 0;
    width: 100%;
    min-height: 280px;
    position: relative;
    isolation: isolate;
  }

  .wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    /*
      Keep all experience boxes above the center timeline.
    */
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    h3 {
      font-size: 24px;
    }

    .parent {
      padding: 0;
      min-height: auto;
    }

    .indicator {
      display: none !important;
    }

    .box-cover,
    .box {
      width: 100% !important;
    }

    .box {
      cursor: pointer !important;
    }
  }
`;

const Slider = styled.div`
  /*
    Timeline stays behind all experience cards.
  */
  position: absolute;
  z-index: 0;

  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);

  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  gap: 5px;

  pointer-events: none;

  span {
    display: block;
    width: 5px;
    height: 5px;
    background: rgb(27, 27, 27);
    border-radius: 4px;
  }

  span:first-of-type {
    flex: 1;
  }

  span:nth-of-type(2) {
    height: 10px;
  }

  span:nth-of-type(3) {
    height: 8px;
  }

  @media (max-width: 768px) {
    display: none !important;
  }
`;

const BoxContainer = styled.div`
  width: 100%;
  display: flex;
  padding-bottom: 100px;

  /*
    Each experience item also sits above the timeline.
  */
  position: relative;
  z-index: 2;

  .box-cover {
    width: 50%;
    position: relative;
    z-index: 2;

    .indicator {
      position: absolute;
      z-index: 4;

      width: 23px;
      height: 23px;
      border-radius: 50%;
      top: -4px;

      background-color: rgb(14, 14, 14);
      border: 4px solid rgb(85, 85, 85);
    }
  }

  .multiple-roles {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;

    svg {
      color: #e5e5e5;
      font-size: 12px;
      flex-shrink: 0;
    }

    span:last-child {
      font-size: 12px;
      font-weight: 200;
      color: #e5e5e5;
      font-style: italic;
    }

    .separate {
      color: #898989;
      margin: 0 2px;
    }
  }

  .box {
    padding: 0 24px;
    width: calc(100% - 25px);

    box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
    background-color: rgb(14, 14, 14);

    border-radius: 10px;
    border: 1px solid #3d3d3d;

    cursor: pointer;

    position: relative;
    z-index: 3;

    /*
      Keeps animated content contained inside the rounded card,
      while the timeline remains underneath the whole card.
    */
    overflow: hidden;
    isolation: isolate;

    .less-desc {
      padding: 20px 0;
      display: flex;
      flex-direction: column;
      gap: 5px;

      position: relative;
      z-index: 2;
    }

    .where-how-long {
      span {
        color: #fff;
        display: block;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      span:first-of-type {
        width: 100%;
        font-weight: 500;
        font-size: 18px;

        display: flex;
        align-items: center;

        line-height: 20px;
        color: #fff;

        margin-bottom: 15px;
        gap: 12px;

        svg {
          font-size: 26px;
          flex-shrink: 0;
        }
      }

      span:last-of-type {
        width: 100%;
        font-size: 12px;
        font-weight: 200;
        font-style: italic;
        color: #abababff;
        margin-top: 5px;
      }
    }

    span {
      font-size: 15px;
      display: block;
      color: #fff;
      font-weight: 400;
    }

    .mini-max-ico {
      position: absolute;
      z-index: 5;

      top: 8px;
      right: 10px;

      color: #939393;
      font-size: 16px;
    }

    .work-done {
      position: relative;
      z-index: 2;

      overflow: hidden;

      /*
        Important: gives expanded content the same solid
        background as the card so nothing behind it can
        visually show through.
      */
      background-color: rgb(14, 14, 14);

      .work-done-inner {
        position: relative;
        z-index: 2;
        background-color: rgb(14, 14, 14);
      }

      p {
        position: relative;
        z-index: 2;

        line-height: 1.8em;
        font-size: 14px;
        color: #abababff;
        padding-bottom: 20px;

        background-color: rgb(14, 14, 14);
      }

      .techs {
        position: relative;
        z-index: 2;

        list-style: none;
        text-decoration: none;

        margin-top: 3px;
        margin-bottom: 0;
        padding-left: 0;
        padding-bottom: 15px;

        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        background-color: rgb(14, 14, 14);
      }

      .techs li {
        font-size: 11px;
        border: 1px solid #abababff;
        border-radius: 5px;
        padding: 3px 8px;

        color: #000;
        flex-shrink: 0;
        background: #abababff;
      }

      .courses {
        font-size: 12px;
        line-height: 1.8em;

        span {
          display: inline;
          text-decoration: underline;
          margin: 0 10px 2px 0;
          font-size: 12px;
          color: #e5e5e5;
        }
      }
    }
  }

  &:nth-child(odd) {
    justify-content: flex-end;

    .box {
      margin-left: auto;
    }

    .indicator {
      left: -11px;
    }
  }

  &:nth-child(even) {
    justify-content: flex-start;

    .box {
      margin-right: auto;
    }

    .indicator {
      right: -12px;
    }
  }

  @media (max-width: 768px) {
    padding-bottom: 50px;

    .where-how-long svg {
      font-size: 16px !important;
    }

    .where-how-long span {
      font-size: 14px !important;
    }

    .work-done {
      p {
        font-size: 12px !important;
      }
    }

    .techs li {
      font-size: 10px !important;
    }

    .less-desc {
      padding: 20px 0 !important;
    }

    .less-desc span {
      font-size: 12px !important;
    }
  }
`;
